import React from 'react';
import { Paper, Tooltip, Typography } from '@mui/material';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import { isAfter, parseISO, format } from 'date-fns';
import { useHistory, useLocation } from 'react-router';

import { PayrollRunFlatFileStateType } from 'generated/graphql';
import { URL_PARAM_PAYROLLRUN } from 'containers/PayrollFlatFilePage/consts';
import { UserCurrencies, currencyFormat } from 'utils/currencyFormat';
import {
  black,
  primaryBlue,
  white,
  blueGrayLight,
  gray2,
  statusNeutral,
  statusTextNeutral,
  statusCompleted,
  statusTextCompleted,
} from 'utils/color-styles';
import Status from 'retired/Status';
import CalendarRoundedIcon from 'assets/icons/calendar-round.svg';
import WalletIcon from 'assets/icons/wallet.svg';

interface PayrollRun {
  id: string;
  startDate: string;
  endDate: string;
  startDateExternal?: string;
  endDateExternal?: string;
  stats: {
    isDonatable: boolean;
    totalUsd: number;
    donationCount?: number | null;
  };
  runState: PayrollRunFlatFileStateType;
}

type Props = {
  payrollRun: PayrollRun;
};

export const CARD_WIDTH = 270;

const PayrollRunHeaderStyle: React.CSSProperties = {
  display: 'inline-flex',
  justifyContent: 'end',
  width: `${CARD_WIDTH}px`,
  letterSpacing: '1.42px',
  whiteSpace: 'nowrap',
};

const PayrollRunFooterStyle: React.CSSProperties = {
  display: 'inline-flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  width: '100%',
};

const PayrollRunCutOffDateStyle: React.CSSProperties = {
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  fontSize: 10,
  color: gray2,
};

const formatDate = (date: Date | number) => format(date, 'MMM dd, yyyy').toUpperCase();

const TOOLTIP_AS_OF_TODAY = `Upcoming Donations as of ${formatDate(new Date())}`;

const getPayrollRunPeriodText = ({
  startDateExternal,
  endDateExternal,
  startDate,
  endDate,
}: PayrollRun): string => {
  const displayStartDate = startDateExternal || startDate;
  const displayEndDate = endDateExternal || endDate;

  return `${formatDate(parseISO(displayStartDate))} —
${formatDate(parseISO(displayEndDate))}`;
};

export const PayrollRunInfo = ({ payrollRun }: Props) => {
  const location = useLocation();
  const history = useHistory();

  const urlSearchParams = new URLSearchParams(location.search);
  const isSelected = payrollRun.id === urlSearchParams.get(URL_PARAM_PAYROLLRUN);
  const isUpcoming = payrollRun.runState === PayrollRunFlatFileStateType.New;
  const isDisabled =
    isUpcoming &&
    !payrollRun.stats.isDonatable &&
    isAfter(new Date(payrollRun.startDate), new Date());

  const deductionIntent = payrollRun.stats.totalUsd;
  const payrollRunPeriod = getPayrollRunPeriodText(payrollRun);

  const handleClick = () => {
    if (isSelected) {
      urlSearchParams.delete(URL_PARAM_PAYROLLRUN);
    } else {
      urlSearchParams.set(URL_PARAM_PAYROLLRUN, payrollRun.id);
    }
    history.replace({
      pathname: location.pathname,
      search: urlSearchParams.toString(),
    });
  };

  return (
    <Tooltip title={isUpcoming && !isDisabled ? TOOLTIP_AS_OF_TODAY : ''}>
      <Paper
        onClick={isDisabled ? undefined : handleClick}
        style={{
          borderRadius: '16px',
          backgroundColor: isSelected ? primaryBlue : blueGrayLight,
          opacity: isDisabled ? 0.5 : 1,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        <Status
          title={payrollRunPeriod}
          color={isUpcoming ? statusNeutral : statusCompleted}
          textColor={isUpcoming ? statusTextNeutral : statusTextCompleted}
          icon={isUpcoming ? CalendarRoundedIcon : WalletIcon}
          style={PayrollRunHeaderStyle}
        />
        <div style={{ padding: '12px 16px 16px', color: isSelected ? white : black }}>
          <Typography style={{ fontSize: 12 }}>
            {isUpcoming ? 'Upcoming' : 'Completed'} Payroll Deductions
          </Typography>
          <Typography style={{ fontSize: 24, fontWeight: 'bold' }}>
            {currencyFormat(deductionIntent, UserCurrencies.USD)}
            {isUpcoming && '*'}
          </Typography>
          <div style={PayrollRunFooterStyle}>
            <Typography style={{ fontSize: 12, color: gray2 }}>
              {payrollRun.stats?.donationCount || 0} donation
              {payrollRun.stats?.donationCount === 1 ? '' : 's'}
            </Typography>
            <Typography style={PayrollRunCutOffDateStyle}>
              <span>cut-off date</span>
              <span>
                <ContentCutIcon style={{ fontSize: 12 }} />{' '}
                {formatDate(parseISO(payrollRun.endDate))}
              </span>
            </Typography>
          </div>
        </div>
      </Paper>
    </Tooltip>
  );
};
